import React, { useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Link,
    List,
    ListItem,
    ListItemText,
    useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import useParallaxEffect from '../common/useParallaxEffect';
import ScrollToTop from '../common/ScrollToTop';

const Section = ({ title, children }) => {
    const theme = useTheme();
    return (
        <Box sx={{ mb: 4 }}>
            <Typography
                variant="h5"
                component="h2"
                gutterBottom
                sx={{ fontWeight: 700, color: theme.palette.secondary.main, mb: 2 }}
            >
                {title}
            </Typography>
            {children}
        </Box>
    );
};

const BulletList = ({ items }) => (
    <List dense disablePadding sx={{ pl: 1 }}>
        {items.map((item, index) => (
            <ListItem key={index} alignItems="flex-start" sx={{ py: 0.25, display: 'list-item', listStyleType: 'disc', ml: 3, pl: 0.5 }}>
                <ListItemText
                    primary={item}
                    primaryTypographyProps={{ variant: 'body1', color: 'text.primary' }}
                />
            </ListItem>
        ))}
    </List>
);

const Body = ({ children, sx }) => (
    <Typography variant="body1" paragraph sx={{ color: 'text.primary', lineHeight: 1.7, ...sx }}>
        {children}
    </Typography>
);

const PrivacyPolicy = () => {
    useParallaxEffect();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <div id="back-to-top-anchor" />
            <Box>
                <Box sx={{ bgcolor: '#f5f5f5', pt: '64px', pb: '30px' }}>
                    <Container maxWidth="lg">
                        <Typography variant="h3" component="h1" align="center" sx={{ color: '#333', fontWeight: 700, mb: 1 }}>
                            Privacy Policy
                        </Typography>
                        <Typography variant="body1" align="center" sx={{ color: '#555' }}>
                            Effective Date: May 1, 2025
                        </Typography>
                        <Typography variant="body1" align="center" sx={{ color: '#555' }}>
                            Last Updated: August 8, 2026
                        </Typography>
                    </Container>
                </Box>

                <Container maxWidth="md" sx={{ py: 5 }}>
                    <Body>
                        Pivotal Behavioral Services (“we,” “us,” or “our”) provides in-home applied behavior analysis
                        (“ABA”) and related services. This Privacy Policy explains how we collect, use, disclose,
                        and protect information through our website and other communication channels.
                    </Body>

                    <Body>
                        This Privacy Policy applies to information collected through the Website, including
                        contact forms, service inquiry forms, employment applications, newsletter registrations,
                        and text-message enrollment forms.
                    </Body>
                    <Body>
                        It does not replace our Notice of Privacy Practices, which explains how we may use and
                        disclose protected health information (“PHI”) under the Health Insurance Portability and
                        Accountability Act (“HIPAA”) and other applicable health-privacy laws.
                    </Body>
                    <Body>
                        You may review our Notice of Privacy Practices by contacting us at{' '}
                        <Link href="mailto:privacyofficer@pivotalbs.com">privacyofficer@pivotalbs.com</Link>.
                    </Body>

                    <Section title="Information We Collect">
                        <Body>Depending on how you use the Website, we may collect:</Body>
                        <BulletList
                            items={[
                                'Contact information, such as your name, telephone number, email address, mailing address, and preferred contact method.',
                                'Service inquiry information, such as the individual’s age range, general location, insurance carrier, services requested, referral source, and availability.',
                                'Information you submit in forms or communications, including questions, messages, and documents.',
                                'Employment information, such as work history, education, professional credentials, references, résumé, and application responses.',
                                'Text-message enrollment information, including your mobile number, consent records, and messaging preferences.',
                                'Device and usage information, such as IP address, browser type, device type, pages viewed, links selected, referring pages, and approximate location derived from an IP address.',
                                'Cookie and analytics information described below.',
                            ]}
                        />
                        <Body sx={{ mt: 2 }}>
                            Please do not submit detailed medical, behavioral, diagnostic, treatment, or other
                            sensitive information through a general website form unless the form is specifically
                            identified as a secure channel for that purpose.
                        </Body>
                    </Section>

                    <Section title="How We Collect Information">
                        <Body>We may collect information:</Body>
                        <BulletList
                            items={[
                                'Directly from you when you complete a form, contact us, apply for employment, or enroll in communications.',
                                'Automatically through cookies, analytics tools, server logs, and similar technologies.',
                                'From referral sources, caregivers, insurers, healthcare professionals, schools, government programs, or other parties when permitted by law.',
                                'From service providers that help us operate the Website and our business.',
                            ]}
                        />
                    </Section>

                    <Section title="How We Use Information">
                        <Body>We may use information to:</Body>
                        <BulletList
                            items={[
                                'Respond to questions and requests.',
                                'Determine whether our services may be available in your area.',
                                'Schedule consultations, assessments, or services.',
                                'Verify insurance benefits or coordinate payment when authorized.',
                                'Communicate with parents, guardians, clients, applicants, referral sources, and other authorized individuals.',
                                'Send service-related text messages.',
                                'Process employment applications.',
                                'Detect fraud, misuse, security incidents, or technical problems.',
                                'Maintain business records and comply with legal, regulatory, licensing, accreditation, Medicaid, insurance, or contractual requirements.',
                                'Protect the rights, safety, and property of our clients, workforce, organization, and others.',
                            ]}
                        />
                        <Body sx={{ mt: 2 }}>
                            We will use PHI only as permitted by our Notice of Privacy Practices and applicable law.
                        </Body>
                    </Section>

                    <Section title="How We Disclose Information">
                        <Body>We may disclose information:</Body>
                        <BulletList
                            items={[
                                'To service providers that support website hosting, communications, scheduling, analytics, information technology, document storage, payment processing, recruiting, or security.',
                                'To insurers, government healthcare programs, referral partners, and healthcare professionals when authorized or otherwise permitted by law.',
                                'To professional advisers, including attorneys, auditors, accountants, and consultants.',
                                'To government agencies, regulators, courts, or law-enforcement authorities when required or permitted by law.',
                                'To protect a person’s safety or our legal rights.',
                                'In connection with a merger, acquisition, financing, reorganization, or sale of all or part of our organization, subject to applicable privacy laws.',
                                'At your direction or with your authorization.',
                            ]}
                        />
                        <Body sx={{ mt: 2 }}>
                            We do not sell PHI. We do not share mobile opt-in consent or text-message originator data
                            with third parties for their independent marketing or promotional purposes. Information
                            may be provided to vendors that help us deliver text messages, subject to appropriate
                            restrictions.
                        </Body>
                    </Section>

                    <Section title="Cookies and Analytics">
                        <Body>
                            The Website may use cookies, pixels, local storage, and similar technologies to remember
                            preferences, understand Website usage, maintain security, and improve performance.
                        </Body>
                        <Body>
                            We may use different analytic tools or advertising services for the company. These
                            providers may receive device and usage information according to their own privacy policies.
                        </Body>
                        <Body>
                            You can control many cookies through your browser settings and any cookie-preference tool
                            displayed on the Website. Disabling cookies may affect Website functionality.
                        </Body>
                    </Section>

                    <Section title="Text Messages">
                        <Body>
                            If you enroll in text messaging, we will process your mobile number, consent record,
                            message content, and messaging activity to operate the program.
                        </Body>
                        <Body>
                            <strong>SMS Communications:</strong> By consenting to receive SMS communications, you can
                            expect to receive appointment reminders and confirmations, billing and invoice
                            notifications, intake/onboarding updates, updates about company policies, closures, or
                            changes, and occasional announcements regarding new services or events.
                        </Body>
                        <Body>
                            Standard messaging disclosures: messaging frequency may vary; message and data rates may
                            apply; you can opt out at any time by replying “STOP” to any messages or contacting us
                            directly; for assistance, text “HELP”, call{' '}
                            <Link href="tel:7372890339">737-289-0339</Link>, or email us at{' '}
                            <Link href="mailto:info@pivotalbs.com">info@pivotalbs.com</Link>; carriers are not liable
                            for delayed or undelivered messages.
                        </Body>
                        <Body>
                            Consent to receive marketing messages is not a condition of purchasing goods or receiving
                            treatment or services. You may opt out by replying STOP. Because ordinary text messaging
                            may not be encrypted, please avoid sending detailed health or treatment information by
                            text.
                        </Body>
                    </Section>

                    <Section title="Children’s Privacy">
                        <Body>
                            The Website is intended for use by adults, including parents and legal guardians seeking
                            information about services for a child. It is not directed to children under 18, and
                            children should not submit personal information through the Website.
                        </Body>
                        <Body>
                            If you believe a child has submitted information without appropriate parental or guardian
                            involvement, contact us via email at{' '}
                            <Link href="mailto:privacyofficer@pivotalbs.com">privacyofficer@pivotalbs.com</Link>.
                        </Body>
                    </Section>

                    <Section title="Security">
                        <Body>
                            We use reasonable administrative, technical, and physical safeguards designed to protect
                            personal information. Nevertheless, no website, email system, or electronic transmission
                            is completely secure, and we cannot guarantee absolute security.
                        </Body>
                        <Body>
                            Email and ordinary text messaging may not be encrypted. Do not use them for emergencies or
                            to transmit sensitive information unless we have specifically instructed you that the
                            communication method is appropriate.
                        </Body>
                    </Section>

                    <Section title="Data Retention">
                        <Body>
                            We retain information for as long as reasonably necessary for the purposes described in
                            this statement, including providing services, maintaining required records, resolving
                            disputes, enforcing agreements, and satisfying legal, insurance, Medicaid, licensing, tax,
                            and regulatory obligations.
                        </Body>
                        <Body>
                            Retention periods vary according to the type of information and applicable requirements.
                        </Body>
                    </Section>

                    <Section title="Your Choices and Rights">
                        <Body>You may:</Body>
                        <BulletList
                            items={[
                                'Unsubscribe from promotional emails by using the unsubscribe link.',
                                'Withdraw your consent for SMS communications or other services.',
                                'Adjust cookie settings through your browser or our cookie-preference tool, if available.',
                                'Access and review the personal information we hold about you.',
                                'Request corrections to your information.',
                                'Ask us to update certain contact information.',
                                'Request deletion of your information, subject to legal and operational requirements.',
                                'Exercise rights available under applicable state privacy law by contacting us as described below.',
                            ]}
                        />
                        <Body sx={{ mt: 2 }}>
                            Requests involving medical or treatment records should be submitted under the procedures
                            in our Notice of Privacy Practices.
                        </Body>
                        <Body>
                            We may need to verify your identity or authority before completing a request. Certain
                            information may be exempt from a request under applicable law.
                        </Body>
                    </Section>

                    <Section title="Third-Party Websites">
                        <Body>
                            The Website may link to websites or services operated by other organizations. We are not
                            responsible for their privacy, security, or content. Review their policies before providing
                            information.
                        </Body>
                    </Section>

                    <Section title="Changes to This Policy">
                        <Body>
                            We may update this Privacy Policy periodically. The revised policy will be posted
                            with a new “Last Updated” date. Material changes may also be communicated through
                            additional means when required by law.
                        </Body>
                    </Section>

                    <Section title="Contact Us">
                        <Body>For privacy questions or requests, contact:</Body>
                        <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
                            Pivotal Behavioral Services
                            <br />
                            Attn: Privacy Officer
                            <br />
                            5511 Parkcrest Drive Suite 103
                            <br />
                            Austin, Texas 78731
                            <br />
                            Email:{' '}
                            <Link href="mailto:privacyofficer@pivotalbs.com">privacyofficer@pivotalbs.com</Link>
                            <br />
                            Telephone: <Link href="tel:7372890339">737-289-0339</Link>
                        </Typography>
                        <Body>
                            For questions or complaints involving PHI, please use the contact information and
                            procedures in our Notice of Privacy Practices.
                        </Body>
                        <Body>
                            Thank you for trusting Pivotal Behavioral Services. We are committed to safeguarding your
                            privacy and ensuring transparency in how we use your information.
                        </Body>
                        <Body>
                            <Link component={RouterLink} to="/contact">
                                Contact us
                            </Link>{' '}
                            if you have questions about this policy.
                        </Body>
                    </Section>
                </Container>
            </Box>
            <ScrollToTop />
        </>
    );
};

export default PrivacyPolicy;
